import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTicketAlt, 
  faPaperclip,
  faUpload,
  faFile,
} from '@fortawesome/free-solid-svg-icons';
import './ticketDetails.css';

const CreateTicket: React.FC = () => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium'
  });
  const [files, setFiles] = useState<FileList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  
  const { subject, description, priority } = formData;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(e.target.files);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!subject || !description) {
      setError('Please enter all required fields');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const ticketData = new FormData();
      ticketData.append('subject', subject);
      ticketData.append('description', description);
      ticketData.append('priority', priority);
      
      if (files) {
        for (let i = 0; i < files.length; i++) {
          ticketData.append('attachments', files[i]);
        }
      }
      
      console.log('Creating ticket with data:', { subject, description, priority, filesCount: files?.length || 0 });
      
      const res = await axios.post('/api/tickets', ticketData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Ticket created successfully:', res.data);
      
      // Redirect to the ticket details page
      navigate(`/tickets/${res.data.ticket.id}`, { replace: true });
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.message || 'Error creating ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
          Create New Support Ticket
        </h2>
      </div>
      
      <div className="card shadow-sm">
        <div className="card-body p-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="subject" className="form-label">Subject <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="subject"
                name="subject"
                value={subject}
                onChange={handleChange}
                placeholder="Brief summary of your issue"
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                value={description}
                onChange={handleChange}
                placeholder="Detailed description of your issue"
                rows={6}
                required
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="priority" className="form-label">Priority</label>
              <select
                className="form-select priority-select"
                id="priority"
                name="priority"
                value={priority}
                onChange={handleChange}
                aria-label="Select priority"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label htmlFor="attachments" className="form-label d-flex justify-content-between align-items-center">
                <span>
                  <FontAwesomeIcon icon={faPaperclip} className="me-2" />
                  Attachments (Max 5 files)
                </span>
                {files && files.length > 0 && (
                  <span className="badge bg-primary">{files.length} file(s)</span>
                )}
              </label>
              
              <div className="row g-2 align-items-center">
                <div className="col-auto">
                  <div className="file-upload-wrapper mb-0">
                    <input
                      type="file"
                      className="file-upload-input"
                      id="attachments"
                      onChange={handleFileChange}
                      multiple
                      max={5}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="attachments" className="file-upload-button btn btn-outline-primary d-flex align-items-center justify-content-center">
                      <FontAwesomeIcon icon={faUpload} className="me-1" />
                      <span>Choose Files</span>
                    </label>
                  </div>
                </div>
                
                <div className="col">
                  <div className="support-text fw-medium">
                    <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                    Supported: Images, PDFs, documents (max 5MB)
                  </div>
                </div>
              </div>
              
              {files && files.length > 0 && (
                <div className="selected-files mt-2">
                  <div className="row g-2">
                    {Array.from(files).map((file, index) => (
                      <div key={index} className="col-md-6 col-lg-4">
                        <div className="selected-file-item">
                          <FontAwesomeIcon icon={faFile} className="file-icon me-2" />
                          <span className="file-name">{file.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTicket; 